import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, StatusTag } from '~/UIKit';
import { ListItem } from '@mui/material';
import classNames from 'classnames';
import { FormatListBulleted, PauseCircleOutline } from '@mui/icons-material';
import { EventType, IEvent } from '~/Models/DTO/Event.model';
import { useDateFns } from '~/Hooks';
import {
  PLAIN_GREY,
  BORDER_COLOR,
  WHITE,
  BLACK,
  CHARCOAL_GREY,
  DEACTIVATED_GREY,
  CHIP_BACKGROUND_RED,
  CHIP_COLOR_RED,
  CHIP_BACKGROUND_GREEN,
  CHIP_COLOR_GREEN,
} from '~/Constants/Colors.constants';
import { ITrainer } from '~/Models/DTO/Trainer.models';
import { ILocation } from '~/Models/DTO/Location.models';
import { IBookingSummaryEvent } from '~/Models/DTO/Booking.models';
import { CameraSuccess, CameraWarning } from '~/Components/Icons';
import { makeStyles } from '~/UIKit/Style';

export interface EventItemProps {
  bookingInformation: IBookingSummaryEvent;
  breakTime: number;
  event: IEvent;
  trainer?: ITrainer;
  location?: ILocation;
  goToDetail: () => void;
}

interface StylesArgs {
  hasBreak: boolean;
  hasTag: boolean;
  isCancelled: boolean;
  hasIcon: boolean;
  color: string;
}

const useStyles = ({ hasBreak, hasTag, isCancelled, hasIcon, color }: StylesArgs) =>
  makeStyles({
    eventItemWrapper: {
      display: 'flex',
      flexDirection: 'column',
      color: CHARCOAL_GREY,
      minHeight: hasBreak ? 80 : 60,
      backgroundColor: WHITE,
      padding: 0,
      margin: 0,
      '&:hover': {
        backgroundColor: WHITE,
      },
    },
    eventItem: {
      borderTop: `1px solid ${BORDER_COLOR}`,
      display: 'flex',
      width: '100%',
      height: 60 + (hasTag ? 32 : 0),
      borderBottom: `1px solid ${BORDER_COLOR}`,
    },
    eventTimeWrapper: {
      minWidth: 70,
      height: '100%',
      backgroundColor: isCancelled ? DEACTIVATED_GREY : 'rgba(0,132,248,0.1)',
      display: 'flex',
      justifyContent: 'space-evenly',
      alignItems: 'center',
      flexDirection: 'column',
      borderRight: `2px solid ${isCancelled ? PLAIN_GREY : color}`,
    },
    eventTimeText: {
      color: isCancelled ? PLAIN_GREY : CHARCOAL_GREY,
      fontSize: '12px',
      fontWeight: 'bold',
      letterSpacing: '0.5px',
      lineHeight: '16px',
      textAlign: 'right',
    },
    eventTimeTextLower: {
      opacity: 0.55,
    },
    eventInfoWrapper: {
      display: 'flex',
      padding: 10,
      alignItems: 'center',
      justifyContent: 'space-between',
      flexDirection: 'row',
      flexGrow: 1,
      maxWidth: 'calc(100% - 70px)',
    },
    eventNameWrapper: {
      display: 'flex',
      flexDirection: 'column',
      maxWidth: hasIcon ? 'calc(100% - 50px)' : 'calc(100% - 29px)',
    },
    eventNameText: {
      color: isCancelled ? PLAIN_GREY : BLACK,
      fontSize: '16px',
      fontWeight: 'bold',
      letterSpacing: '0.5px',
      lineHeight: '21px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    eventInfoText: {
      color: isCancelled ? PLAIN_GREY : CHARCOAL_GREY,
      fontSize: '12px',
      fontWeight: 300,
      letterSpacing: '0.5px',
      lineHeight: '16px',
    },
    eventInfoTextWrapper: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    eventSeatsText: {
      fontSize: 12,
    },
    waitingListText: {
      display: 'flex',
      fontSize: 12,
      alignItems: 'center',
    },
    waitingListIcon: {
      fontSize: 15,
    },
    eventBreakTime: {
      fontSize: 12,
      display: 'flex',
      alignItems: 'center',
      height: 20,
      background: '#FBFBFE',
      width: '100%',
      '& > svg': {
        height: 15,
      },
    },
    eventInfoIcons: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
    iconWarning: {
      color: CHIP_COLOR_RED,
      backgroundColor: CHIP_BACKGROUND_RED,
      width: 32,
      height: 32,
      marginRight: 16,
      borderRadius: 16,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      '& > svg': {
        fontSize: 24,
      },
    },
    iconStream: {
      color: CHIP_COLOR_GREEN,
      backgroundColor: CHIP_BACKGROUND_GREEN,
      width: 32,
      height: 32,
      borderRadius: 16,
      marginRight: 16,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      '& > svg': {
        fontSize: 24,
      },
    },
    tagWrapper: {
      display: 'flex',
      paddingTop: 8,
    },
  });

export default function EventItem({
  event,
  breakTime = -1,
  location,
  goToDetail,
  bookingInformation,
  trainer,
}: EventItemProps) {
  const source =
    !!event?.source && `${event?.source}`.toLocaleLowerCase() !== 'fitogram' && event?.source;
  const { classes } = useStyles({
    hasBreak: breakTime > 0 && breakTime < 60,
    hasTag: !!source,
    isCancelled: event.cancelled,
    hasIcon: event.livestream,
    color: event.color,
  })();
  const { formatDate } = useDateFns();
  const { t } = useTranslation();

  const eventDetails = useMemo(() => {
    const locationName = location?.name || '';
    if (event.type === EventType.FREE_TRAINING) {
      return `${t('event.freeTraining')} · ${locationName}`;
    }

    const trainerName = trainer
      ? `${trainer?.shortenedFirstName || ''} ${trainer?.shortenedLastName || ''}`
      : '';

    return trainerName ? `${trainerName} · ${locationName}` : locationName;
  }, [event, location]);

  const isTimeVisible = useMemo(() => ![EventType.FREE_TRAINING].includes(event.type), [event]);

  return (
    <ListItem
      button
      className={classes.eventItemWrapper}
      data-testid={`EventItem-${event.id}`}
      onClick={goToDetail}
    >
      <div className={classes.eventItem}>
        <div className={classes.eventTimeWrapper}>
          {isTimeVisible && (
            <>
              <Typography className={classes.eventTimeText} component="span">
                {formatDate(event.start, 'HH:mm')}
              </Typography>
              <Typography
                className={classNames(classes.eventTimeText, classes.eventTimeTextLower)}
                component="span"
              >
                {formatDate(event.end, 'HH:mm')}
              </Typography>
            </>
          )}
        </div>
        <div className={classes.eventInfoWrapper}>
          <div className={classes.eventNameWrapper}>
            <Typography className={classes.eventNameText} component="span">
              {event.name}
            </Typography>
            <span className={classes.eventInfoTextWrapper}>
              {event.cancelled && (
                <Typography className={classes.eventInfoText}>
                  {t('app.event.cancelled')}
                </Typography>
              )}
              {!event.cancelled && !!event.locationId && !!location && (
                <Typography
                  className={classes.eventInfoText}
                  component="span"
                  data-testid={`EventTrainer-${event.id}`}
                >
                  {eventDetails}
                </Typography>
              )}
            </span>
            {!!source && (
              <span className={classes.tagWrapper}>
                <StatusTag status="noStatus" size="medium">
                  {source}
                </StatusTag>
              </span>
            )}
          </div>
          <div className={classes.eventInfoIcons}>
            {event.livestream && !event.virtualClassUrl && (
              <div className={classes.iconWarning}>
                <CameraWarning viewBox="0 0 24 24" data-testid="CameraWarningIcon" />
              </div>
            )}
            {event.livestream && event.virtualClassUrl && (
              <div className={classes.iconStream}>
                <CameraSuccess viewBox="0 0 24 24" data-testid="CameraSuccessIcon" />
              </div>
            )}
            {!event.cancelled &&
              !!event.seats &&
              bookingInformation &&
              !bookingInformation.bookingsWaiting &&
              event.type !== EventType.FREE_TRAINING && (
                <Typography
                  className={classes.eventSeatsText}
                  data-testid={`EventSeats-${event.id}`}
                >
                  {bookingInformation.bookingsBooked - bookingInformation.publicApiClientBookings}/
                  {event.seats}
                </Typography>
              )}
            {!event.cancelled &&
              !!event.seats &&
              bookingInformation &&
              bookingInformation.bookingsWaiting > 0 &&
              event.type !== EventType.FREE_TRAINING && (
                <Typography
                  className={classes.waitingListText}
                  data-testid={`EventSeats-${event.id}`}
                >
                  <FormatListBulleted className={classes.waitingListIcon} />
                  <span>+{bookingInformation.bookingsWaiting}</span>
                </Typography>
              )}
          </div>
        </div>
      </div>
      {isTimeVisible && breakTime > 0 && breakTime <= 120 && (
        <div className={classes.eventBreakTime} data-testid={`EventBreakTime-${event.id}`}>
          <PauseCircleOutline />
          {breakTime} {t('app.time.minutes')}
        </div>
      )}
      {isTimeVisible && breakTime > 120 && (
        <div className={classes.eventBreakTime} data-testid={`EventBreakTime-${event.id}`}>
          <PauseCircleOutline />
          {(breakTime / 60).toFixed(0)} {t('app.time.hours')}
        </div>
      )}
    </ListItem>
  );
}
